import * as stylex from '@stylexjs/stylex'
import { useEffect, useState } from 'react'

import Dialog from './Dialog'
import Icon from './Icon'
import { v1API, type DatabaseSyncJob } from '../lib/ledger'

const spin = stylex.keyframes({
  from: { transform: 'rotate(0deg)' },
  to: { transform: 'rotate(360deg)' },
})

const styles = stylex.create({
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  s1: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#d9e8d7",
    backgroundColor: "#f3f8f1",
    padding: "16px",
    fontSize: "13px",
    lineHeight: "1.25rem",
    color: "#45604b",
  },
  s2: {
    marginTop: "8px",
  },
  s3: {
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#edd8d0",
    backgroundColor: "#fff5f1",
    paddingInline: "12px",
    paddingBlock: "8px",
    fontSize: "13px",
    color: "#a35335",
  },
  s4: {
    display: "flex",
    justifyContent: "end",
    gap: "12px",
  },
  s5: {
    height: "40px",
    borderRadius: "0.75rem",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#5b6b60",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f0f4ef",
    },
  },
  s6: {
    display: "flex",
    height: "40px",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#438b57",
    paddingInline: "20px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347845",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },
  s7: {
    height: "16px",
    width: "16px",
    borderRadius: "9999px",
    borderWidth: 2,
    borderColor: "color-mix(in srgb, #fff 40%, transparent)",
    borderTopColor: "#fff",
    animationName: spin,
    animationDuration: "1s",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
  },
  s8: {
    height: "16px",
    width: "16px",
  },
  s9: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#edd8d0",
    backgroundColor: "#fff5f1",
    padding: "16px",
    fontSize: "13px",
    lineHeight: "1.25rem",
    color: "#8c4933",
  },
  s10: {
    marginTop: "4px",
    color: "#a46755",
  },
  s11: {
    marginTop: "8px",
    fontSize: "12px",
    color: "#9b6b5c",
  },
  s12: {
    fontSize: "14px",
    color: "#536158",
  },
  s13: {
    height: "12px",
    overflow: "hidden",
    borderRadius: "9999px",
    backgroundColor: "#e5ece3",
  },
  s14: {
    height: "100%",
    borderRadius: "9999px",
    backgroundColor: "#4b9160",
    transition: "width",
    transitionDuration: "300ms",
  },
  s15: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "13px",
    fontWeight: 600,
    color: "#45604b",
  },
  s16: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e3ebe1",
    backgroundColor: "#fbfdfb",
    padding: "16px",
    fontSize: "12px",
    color: "#657268",
  },
  s17: {
    textTransform: "uppercase",
    letterSpacing: ".12em",
    color: "#91a095",
  },
  s18: {
    marginTop: "4px",
    fontWeight: 600,
    color: "#39493e",
  },
  s19: {
    fontSize: "13px",
    color: "#536158",
  },
  s20: {
    height: "40px",
    borderRadius: "0.75rem",
    backgroundColor: "#438b57",
    paddingInline: "20px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347845",
    },
  },
})
type DatabaseSyncDialogProps = {
  targetEnvironment: 'development' | 'staging'
  onClose: () => void
  onCompleted: () => void
  startSync?: () => Promise<DatabaseSyncJob>
  getSync?: (syncID: string) => Promise<DatabaseSyncJob>
  initialJob?: DatabaseSyncJob | null
}

const defaultStartSync = () => v1API.startDatabaseSync()
const defaultGetSync = (syncID: string) => v1API.getDatabaseSync(syncID)

const phaseLabels: Record<DatabaseSyncJob['phase'], string> = {
  PREPARING: 'Preparing production snapshot',
  DOWNLOADING: 'Downloading production snapshot',
  VERIFYING: 'Verifying production snapshot',
  APPLYING: 'Applying production snapshot',
  COMPLETED: 'Production data synced',
  FAILED: 'Sync failed',
}

function environmentLabel(value: string) {
  return value === 'development' ? 'Development' : 'Staging'
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

function formatSnapshotDate(value?: string) {
  if (!value) return ''
  return new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

export default function DatabaseSyncDialog({ targetEnvironment, onClose, onCompleted, startSync = defaultStartSync, getSync = defaultGetSync, initialJob }: DatabaseSyncDialogProps) {
  const [job, setJob] = useState<DatabaseSyncJob | undefined>(initialJob ?? undefined)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState('')
  const targetLabel = environmentLabel(targetEnvironment)
  const active = Boolean(job && job.phase !== 'COMPLETED' && job.phase !== 'FAILED')
  const canClose = !active
  const jobID = job?.id
  const phase = job?.phase

  useEffect(() => {
    if (!jobID || phase === 'COMPLETED' || phase === 'FAILED') return undefined
    let cancelled = false
    const poll = async () => {
      try {
        const next = await getSync(jobID)
        if (!cancelled) {
          setJob(next)
          setError('')
        }
      } catch (reason) {
        if (!cancelled) setError(reason instanceof Error ? reason.message : 'Progress is temporarily unavailable.')
      }
    }
    const interval = window.setInterval(() => { void poll() }, 900)
    void poll()
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [getSync, jobID, phase])

  async function begin() {
    setStarting(true)
    setError('')
    try {
      setJob(await startSync())
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'We could not start the synchronization.')
    } finally {
      setStarting(false)
    }
  }

  function retry() {
    setJob(undefined)
    setError('')
  }

  const title = job ? phaseLabels[job.phase] : 'Sync production data'
  return (
    <Dialog closeDisabled={!canClose} description="Replace this environment with a verified, read-only production snapshot." onClose={canClose ? onClose : () => undefined} title={title}>
      {!job && <div {...stylex.props(styles.s0)}>
        <div {...stylex.props(styles.s1)}>
          <p>This will replace all <strong>{targetLabel}</strong> data with the current production data.</p>
          <p {...stylex.props(styles.s2)}>Production will not be modified. Any changes made only in {targetLabel.toLowerCase()} will be overwritten.</p>
        </div>
        {error && <p {...stylex.props(styles.s3)} role="alert">{error}</p>}
        <div {...stylex.props(styles.s4)}>
          <button {...stylex.props(styles.s5)} onClick={onClose} type="button">Cancel</button>
          <button {...stylex.props(styles.s6)} disabled={starting} onClick={() => { void begin() }} type="button">{starting && <span {...stylex.props(styles.s7)} />}{starting ? 'Starting…' : 'Sync now'}<Icon {...stylex.props(styles.s8)} name="arrow-right" /></button>
        </div>
      </div>}

      {job && <div {...stylex.props(styles.s0)}>
        {job.phase === 'FAILED' && <div {...stylex.props(styles.s9)} role="alert"><p>{job.message}</p><p {...stylex.props(styles.s10)}>Your existing {targetLabel.toLowerCase()} data was not changed.</p>{job.error && <p {...stylex.props(styles.s11)}>{job.error}</p>}</div>}
        {job.phase !== 'FAILED' && job.phase !== 'COMPLETED' && <p {...stylex.props(styles.s12)}>{job.message}</p>}
        <div aria-label={`Synchronization progress: ${job.percent}%`} aria-valuemax={100} aria-valuemin={0} aria-valuenow={job.percent} {...stylex.props(styles.s13)} role="progressbar"><div {...stylex.props(styles.s14)} style={{ width: `${Math.max(0, Math.min(100, job.percent))}%` }} /></div>
        <div {...stylex.props(styles.s15)}><span>{job.percent}%</span><span>{formatBytes(job.downloadedBytes)}{job.totalBytes != null ? ` / ${formatBytes(job.totalBytes)}` : ''}</span></div>
        <div {...stylex.props(styles.s16)}><div><p {...stylex.props(styles.s17)}>Source</p><p {...stylex.props(styles.s18)}>Production</p></div><div><p {...stylex.props(styles.s17)}>Target</p><p {...stylex.props(styles.s18)}>{targetLabel}</p></div></div>
        {job.phase === 'COMPLETED' && <p {...stylex.props(styles.s19)}>{targetLabel} is now in sync with production.{job.snapshotGeneratedAt && <><br />Snapshot: {formatSnapshotDate(job.snapshotGeneratedAt)}</>}</p>}
        <div {...stylex.props(styles.s4)}><button {...stylex.props(styles.s5)} onClick={job.phase === 'FAILED' ? onClose : onCompleted} type="button">{job.phase === 'FAILED' ? 'Close' : 'Done'}</button>{job.phase === 'FAILED' && <button {...stylex.props(styles.s20)} onClick={retry} type="button">Try again</button>}</div>
      </div>}
    </Dialog>
  )
}
